import { ComponentChildren } from 'preact';
import { AsyncForm } from '@components/async-form';
import { useState } from 'preact/hooks';
import { useConfiguration, useCurrentTenant } from '@components/router/session-context';
import { IcoChevronDown, IcoRuzukuLogo, IcoTrash, IcoExternalLink } from '@components/icons';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { showError } from '@components/app-error';
import { imageUrl } from 'client/utils/cdn';
import { BtnCopy, BtnPreWarning, BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { useUnsavedWarning } from '@components/autosaver';
import { BrandRow } from 'server/types';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { ModalForm, showModalForm } from '@components/modal-form';
import { Dropdown } from '@components/dropdown';
import { pickLogo } from './pick-logo';
import { GeneralPreview } from './general-preview';
import { LoginPreview } from './login-preview';
import { CoursePreview } from './course-preview';
import { showDialog } from '@components/dialog';
import { AppRouter, defRoute } from '@components/router';
import { rpx } from 'client/lib/rpx-client';
import { Brand, Props, load } from './load';
import { fullyQualify } from 'client/utils/url';

export const route = defRoute({
  authLevel: 'admin',
  key(x) {
    return x.params.brandId;
  },
  load,
  Page,
});

function toImageState(fileId?: UUID, publicUrl?: string) {
  if (!fileId || !publicUrl) {
    return undefined;
  }
  return { fileId, publicUrl };
}

function createBrand(router: AppRouter) {
  return showModalForm(({ resolve }) => {
    const hide = () => resolve(undefined);
    const submit = async (data: any) => {
      try {
        const opts = {
          name: data.name || 'Unnamed Brand',
        };
        const { id } = await rpx.branding.create(opts);
        router.goto(`/admin/branding/${id}`);
        hide();
      } catch (err) {
        showError(err);
      }
    };

    return (
      <ModalForm
        onClose={hide}
        onSubmit={submit}
        title="New brand"
        confirmButtonText="Create brand"
      >
        <label class="flex flex-col">
          <span>Brand name</span>
          <input type="text" placeholder="Brand name" name="name" class="ruz-input" />
        </label>
      </ModalForm>
    );
  });
}

function SectionTitle(props: { title: ComponentChildren }) {
  return <h3 class="font-medium text-xl mb-4">{props.title}</h3>;
}

async function deleteBrand(brand: Pick<BrandRow, 'id' | 'name'>, onDeleted: () => void) {
  try {
    const ok = await showDialog({
      mode: 'warn',
      title: `Delete ${brand.name}?`,
      children: `Any affected courses will become part of the default brand.`,
      confirmButtonText: `Delete Brand`,
    });
    if (!ok) {
      return;
    }
    await rpx.branding.remove({ id: brand.id });
    onDeleted();
  } catch (err) {
    showError(err);
  }
}

function Page(props: Props) {
  const tenant = useCurrentTenant();
  const config = useConfiguration()!;
  const [faviconUrl, setFaviconUrl] = useState(() => {
    return toImageState(tenant.faviconId, imageUrl(config, tenant.faviconUrl))?.publicUrl;
  });

  const { setState } = props;
  const { brands, supportsBrands, fonts, defaultFontId, colorSchemes, defaultColorSchemeId } =
    props.state;
  const brand =
    brands.find((x) => x.id === props.params.brandId) || brands.find((x) => x.id === tenant.id);
  const [savedState, setSavedState] = useState(brand);
  const hasUnsavedChanges = savedState !== brand;
  const warnOnReload = { shouldWarn: hasUnsavedChanges };
  const isDefaultBrand = tenant.id === brand?.id;
  const previewHref = `/login?preview=true&brandId=${brand?.id}`;

  useUnsavedWarning(() => warnOnReload.shouldWarn);

  const setBrand = (fn: (b: Brand) => Brand) => {
    if (brand) {
      setState((s) => ({
        ...s,
        brands: s.brands.map((b) => (b.id === brand.id ? fn(b) : b)),
      }));
    }
  };

  const saveChanges = async () => {
    if (brand) {
      await rpx.branding.update(brand);
    }
    setSavedState(brand);
  };

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="branding" pageTitle="Branding" />
        <AdminTabWrapper>
          <AdminTabHeader
            title={
              <span>
                <span class="mr-4">Branding:</span>
                <Dropdown
                  triggerClass="text-gray-500"
                  renderMenu={() => {
                    return (
                      <nav class="flex flex-col min-w-80 space-y-2 p-4">
                        {brands.map((b) => (
                          <Button
                            key={b.id}
                            class={`flex items-center py-1 p-2 rounded-md hover:bg-gray-100 ${
                              b.id === brand?.id ? 'bg-gray-100' : ''
                            }`}
                            href={`/admin/branding/${b.id}`}
                          >
                            <span class="inline-block px-2">
                              {b.iconUrl && <img class="rounded-full h-10 w-10" src={b.iconUrl} />}
                              {!b.iconUrl && (
                                <span class="inline-block bg-gray-100 rounded-full h-10 w-10"></span>
                              )}
                            </span>
                            <span class="inline-block align-middle p-2">{b.name}</span>
                          </Button>
                        ))}
                      </nav>
                    );
                  }}
                >
                  {brand?.name || 'Choose Brand'}
                </Dropdown>
              </span>
            }
          >
            <BtnPrimary onClick={() => createBrand(props.router)}>Create Brand</BtnPrimary>
          </AdminTabHeader>

          {brand && (
            <AsyncForm
              class="pb-20 mb-20 w-5xl max-w-full mx-auto"
              onSubmit={async () => {
                try {
                  await saveChanges();
                  if (isDefaultBrand) {
                    setTimeout(() => location.reload());
                  }
                } catch (err) {
                  showError(err);
                }
              }}
            >
              <div class="mb-8 p-4 bg-gray-50 shadow-sm text-gray-700 rounded-lg">
                {!isDefaultBrand && (
                  <p>
                    This is a custom brand. You can apply custom brands to a course in the course
                    style editor.
                  </p>
                )}
                {isDefaultBrand && (
                  <p>
                    This is the default brand. The style changes you make here will apply to your
                    entire site.
                  </p>
                )}
              </div>

              {!isDefaultBrand && (
                <div>
                  <SectionTitle title="Brand settings" />

                  <div class="border rounded-sm p-2 mb-8">
                    <div class="w-full rounded-xs flex flex-col bg-gray-50 p-4">
                      <div class="flex justify-between">
                        <div class="flex items-end gap-4">
                          <label class="inline-flex flex-col">
                            <span>Brand name</span>
                            <input
                              class="inline-ruz-input border p-1 px-2"
                              name="name"
                              value={brand.name}
                              onInput={(e: any) => {
                                setState((s) => ({
                                  ...s,
                                  brands: s.brands.map((b) =>
                                    b.id === brand.id ? { ...b, name: e.target.value } : b,
                                  ),
                                }));
                              }}
                            />
                          </label>

                          <BtnCopy
                            value={fullyQualify(`/login?brandId=${brand.id}`)}
                            margin="p-1.5 rounded-md hover:bg-gray-200 transition-all"
                          >
                            Copy branded login link
                          </BtnCopy>
                        </div>
                        <div class="flex items-end gap-4">
                          <BtnPreWarning
                            class="px-4 border-none"
                            onClick={() => {
                              deleteBrand(brand, () => {
                                location.assign(`/admin/branding`);
                              });
                            }}
                          >
                            <IcoTrash class="w-4 h-4 mr-2" />
                            Delete Brand
                          </BtnPreWarning>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <SectionTitle title="Site header" />

              {isDefaultBrand && (
                <div class="rounded-t border inline-flex items-center ml-4">
                  <Button
                    type="button"
                    class="cursor-pointer focus:outline-hidden bg-white border-b-0 -mb-0.5 relative z-10 focus:ring-2 focus:ring-indigo-600 inline-flex items-center justify-center p-2"
                    onClick={async () => {
                      const favicon = await pickLogo();
                      if (favicon) {
                        setFaviconUrl(favicon.publicUrl);
                        setBrand((s) => ({ ...s, faviconId: favicon.fileId }));
                      }
                    }}
                  >
                    <span class="w-4 h-4 mr-2 inline-flex items-center">
                      {!!faviconUrl && <img src={faviconUrl} class="w-full" />}
                      {!faviconUrl && <IcoRuzukuLogo class="w-full h-full" />}
                    </span>
                    Change your favicon (32x32 px)
                    <IcoChevronDown class="w-4 h-4 ml-2 opacity-75" />
                  </Button>
                </div>
              )}
              <div class="border rounded-sm p-2 mb-8">
                <GeneralPreview brand={brand} setBrand={setBrand} />
              </div>

              <div class="flex items-center justify-between mb-4">
                <SectionTitle title="Login page" />
                <Button
                  onClick={
                    hasUnsavedChanges
                      ? async (e) => {
                          e.preventDefault();
                          const ok = await showDialog({
                            mode: 'info',
                            title: 'Save first?',
                            children: (
                              <>
                                Your changes aren't live yet. Would you like to save before viewing
                                the live login screen?
                              </>
                            ),
                            confirmButtonText: 'Yes. Save my changes.',
                            cancelButtonText: 'No. Take me to the login screen.',
                          });
                          if (ok) {
                            try {
                              await saveChanges();
                              window.open(previewHref, '_blank');
                            } catch (err) {
                              showError(err);
                            }
                          }
                        }
                      : undefined
                  }
                  href={previewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
                >
                  <span>View Live</span>
                  <IcoExternalLink class="size-4" />
                </Button>
              </div>
              <div class="border rounded-sm p-2 mb-8">
                <LoginPreview brand={brand} setBrand={setBrand} />
              </div>

              <SectionTitle title="Course branding" />
              <div class="border rounded-sm p-2">
                <CoursePreview
                  supportsBrands={supportsBrands}
                  brand={brand}
                  setBrand={setBrand}
                  fonts={fonts}
                  defaultFontId={defaultFontId}
                  colorSchemes={colorSchemes}
                  defaultColorSchemeId={defaultColorSchemeId}
                />
              </div>

              {hasUnsavedChanges && (
                <footer class="fixed bottom-0 p-6 pb-8 w-full border-t shadow-2xl drop-shadow-2xl left-0 bg-white an-slide-up">
                  <div class="max-w-5xl mx-auto flex items-center gap-4">
                    <BtnPrimary>Save changes</BtnPrimary>
                    <BtnSecondary
                      onClick={() => {
                        warnOnReload.shouldWarn = false;
                        location.assign(location.href);
                      }}
                    >
                      Cancel
                    </BtnSecondary>
                  </div>
                </footer>
              )}
            </AsyncForm>
          )}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
